import { useDashboard } from '../hooks';
import { StatCard, PageHeader } from '../components/UI';
import { Users, GraduationCap, DollarSign, CheckSquare, AlertTriangle, Calendar, Bell } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { format } from 'date-fns';

const COLORS = ['#1a73e8', '#0f9d58', '#f29900', '#d93025', '#7c4dff', '#00acc1'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e0e7ff', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: '0.82rem' }}>
      <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>
      {payload.map(p => <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>)}
    </div>
  );
};

export default function Dashboard() {
  const { stats, loading } = useDashboard();
  const today = format(new Date(), 'dd MMM yyyy');

  if (loading) return (
    <div className="loading-screen">
      <div className="spinner spinner-dark" />
      <span>Loading Dashboard...</span>
    </div>
  );

  const attPercent = stats?.todayAttendance?.total
    ? Math.round((stats.todayAttendance.present / stats.todayAttendance.total) * 100)
    : 0;

  const feePercent = stats?.fees?.total
    ? Math.round((stats.fees.collected / stats.fees.total) * 100)
    : 0;

  const pieData = stats?.feeStats?.map(f => ({ name: f.status, value: parseFloat(f.amount || 0) })) || [
    { name: 'paid', value: 65000 }, { name: 'pending', value: 25000 }, { name: 'overdue', value: 10000 }
  ];

  const attData = stats?.monthlyAttendance?.length ? stats.monthlyAttendance : [
    { month: 'Aug', pct: 91 }, { month: 'Sep', pct: 88 }, { month: 'Oct', pct: 94 },
    { month: 'Nov', pct: 87 }, { month: 'Dec', pct: 82 }, { month: 'Jan', pct: 90 },
  ];

  const classData = stats?.classDistribution?.length ? stats.classDistribution.map(c => ({
    name: `${c.name} ${c.section}`, strength: c.strength || 0
  })) : [
    { name: 'Class 9A', strength: 45 }, { name: 'Class 9B', strength: 42 },
    { name: 'Class 10A', strength: 48 }, { name: 'Class 10B', strength: 44 },
    { name: 'Class 11 Sci', strength: 40 }, { name: 'Class 12 Sci', strength: 38 },
  ];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`School Overview — ${today}`} />

      {/* Stat Cards */}
      <div className="stat-grid" style={{ marginBottom: 28 }}>
        <StatCard icon={<GraduationCap size={24}/>} label="Total Students" value={stats?.students || 0} sub="Active enrollments" color="blue"/>
        <StatCard icon={<Users size={24}/>} label="Total Teachers" value={stats?.teachers || 0} sub="Teaching staff" color="green"/>
        <StatCard icon={<CheckSquare size={24}/>} label="Today's Attendance" value={`${attPercent}%`} sub={`${stats?.todayAttendance?.present || 0} present / ${stats?.todayAttendance?.absent || 0} absent`} color="purple"/>
        <StatCard icon={<DollarSign size={24}/>} label="Fee Collected" value={`₹${((stats?.fees?.collected || 0)/1000).toFixed(0)}K`} sub={`${feePercent}% of total`} color="orange"/>
      </div>

      {/* Charts Row 1 */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        {/* Attendance trend */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Monthly Attendance Trend</span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Last 6 Months</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={attData}>
              <defs>
                <linearGradient id="attGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a73e8" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#1a73e8" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="month" tick={{ fontFamily: 'Poppins', fontSize: 11 }}/>
              <YAxis domain={[70, 100]} tick={{ fontFamily: 'Poppins', fontSize: 11 }}/>
              <Tooltip content={<CustomTooltip />}/>
              <Area type="monotone" dataKey="pct" stroke="#1a73e8" strokeWidth={2.5} fill="url(#attGrad)" name="Attendance %"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Fee distribution */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Fee Collection Status</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <ResponsiveContainer width="55%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip formatter={(v) => `₹${v.toLocaleString()}`}/>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {pieData.map((entry, i) => (
                <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i], flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'capitalize' }}>{entry.name}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>₹{entry.value?.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        {/* Class strength */}
        <div className="card">
          <div className="card-header"><span className="card-title">Class-wise Strength</span></div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={classData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="name" tick={{ fontFamily: 'Poppins', fontSize: 10 }} angle={-20} textAnchor="end" height={45}/>
              <YAxis tick={{ fontFamily: 'Poppins', fontSize: 11 }}/>
              <Tooltip content={<CustomTooltip />}/>
              <Bar dataKey="strength" fill="#1a73e8" radius={[6,6,0,0]} name="Students"/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Quick info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Notices */}
          <div className="card" style={{ flex: 1 }}>
            <div className="card-header"><span className="card-title">📢 Recent Notices</span></div>
            {stats?.recentNotices?.length ? stats.recentNotices.slice(0, 3).map((n, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: 10, borderBottom: i < 2 ? '1px solid var(--border)' : 'none', marginBottom: 10 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: n.priority === 'urgent' ? 'var(--danger)' : 'var(--primary)', marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{n.title}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>By {n.by}</div>
                </div>
              </div>
            )) : <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>No recent notices</div>}
          </div>

          {/* Upcoming holidays */}
          <div className="card" style={{ flex: 1 }}>
            <div className="card-header"><span className="card-title">🗓 Upcoming Holidays</span></div>
            {stats?.upcomingHolidays?.length ? stats.upcomingHolidays.slice(0,3).map((h, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 8, borderBottom: i < 2 ? '1px solid var(--border)' : 'none', marginBottom: 8 }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{h.name}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{h.date ? format(new Date(h.date), 'dd MMM') : ''}</div>
              </div>
            )) : <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>No upcoming holidays</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
